
import React, { useState, useMemo, useEffect } from 'react';
import { 
  LayoutDashboard, 
  Building2, 
  Users, 
  CreditCard, 
  Search, 
  MoreVertical, 
  AlertTriangle, 
  TrendingUp, 
  LogOut,
  Wifi,
  WifiOff, 
  RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { api, Store, Staff, getTrialStatus } from '../api';

interface AggregatedCompany {
  id: string; 
  name: string;
  representativeName: string;
  stores: Store[];
  storeCount: number;
  userCount: number; 
  totalStaffCount: number;
  joinedDate: string;
  status: 'active' | 'suspended';
  plan: 'standard' | 'business';
  monthlyRevenue: number;
  paymentStatus: 'valid' | 'failed' | 'incomplete' | 'none';
}

const formatDate = (dateStr: string) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}/${m}/${d}`;
  } catch (e) {
    return dateStr.split('T')[0]; // Fallback
  }
};

const StatCard: React.FC<{ title: string; value: string; subValue?: string; icon: React.ReactNode; color: string }> = ({ title, value, subValue, icon, color }) => (
  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-start justify-between">
    <div>
      <p className="text-xs font-bold text-gray-400 mb-1">{title}</p>
      <h3 className="text-2xl font-bold text-slate-800">{value}</h3>
      {subValue && <p className={`text-xs font-bold mt-1 ${subValue.startsWith('+') ? 'text-green-500' : 'text-slate-500'}`}>{subValue}</p>}
    </div>
    <div className={`p-3 rounded-lg ${color} text-white`}>
      {icon}
    </div>
  </div>
);

const SystemAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'suspended'>('all');
  const [companies, setCompanies] = useState<AggregatedCompany[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [stores, staffList] = await Promise.all([
                api.getStores(),
                api.getStaff()
            ]);
            const storesByCompany = stores.reduce((acc, store) => {
                const companyKey = store.companyName || store.name;
                if (!acc[companyKey]) acc[companyKey] = [];
                acc[companyKey].push(store);
                return acc;
            }, {} as Record<string, Store[]>);
            const aggregatedCompanies: AggregatedCompany[] = Object.entries(storesByCompany).map(([companyName, companyStores]) => {
                const primaryStore = companyStores.sort((a,b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime())[0];
                const ownerStaff = staffList.find(s => s.id === primaryStore.ownerId);
                const representativeName = ownerStaff ? `${ownerStaff.lastName} ${ownerStaff.firstName}`.trim() : '不明';
                const storeIdsInCompany = new Set(companyStores.map(s => s.id));
                const staffInCompany = staffList.filter(s => s.stores?.some(storeId => storeIdsInCompany.has(storeId)));
                const billableStaffCount = staffInCompany.filter(s => !s.isGuest).length;
                const companyPlan = primaryStore.plan || 'standard';
                const pricePerUser = companyPlan === 'business' ? 300 : 100;
                const monthlyRevenue = billableStaffCount * pricePerUser;
                return {
                    id: companyName, name: companyName, representativeName: representativeName, stores: companyStores, storeCount: companyStores.length, userCount: billableStaffCount, totalStaffCount: staffInCompany.length, joinedDate: primaryStore.createdAt || new Date().toISOString(), status: 'active', plan: companyPlan, monthlyRevenue, paymentStatus: companyStores.some(s => s.stripeSubscriptionId) ? 'valid' : 'incomplete',
                };
            });
            setCompanies(aggregatedCompanies);
        } catch (e) { console.error(e); } finally { setIsLoading(false); }
    };
    fetchData();
  }, []);

  const stats = useMemo(() => {
    const totalCompanies = companies.length;
    const totalUsers = companies.reduce((acc, c) => acc + c.userCount, 0);
    const totalStaffCount = companies.reduce((acc, c) => acc + c.totalStaffCount, 0);
    const mrr = companies.reduce((acc, c) => acc + c.monthlyRevenue, 0);
    return { totalCompanies, totalUsers, totalStaffCount, mrr };
  }, [companies]);

  const filteredCompanies = companies.filter(c => {
    const lowerSearch = searchTerm.toLowerCase();
    const matchesSearch = c.name.toLowerCase().includes(lowerSearch) || c.representativeName.toLowerCase().includes(lowerSearch);
    const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleLogout = () => navigate('/login');

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans text-slate-700">
      <aside className="w-64 bg-brandDark text-white h-screen fixed left-0 top-0 flex flex-col z-20">
        <div className="p-6 h-20 flex flex-col items-start justify-center">
          <Logo size="sm" textColor="text-white" />
          <div className="ml-1 mt-1 text-[10px] text-gray-300 font-mono px-1 py-0.5 rounded bg-gray-700">SYSTEM ADMIN</div>
        </div>
        <nav className="flex-1 px-4 mt-4 space-y-2">
           <div className="flex items-center px-4 py-3 bg-blue-600 rounded-lg text-white font-bold shadow-md cursor-pointer">
             <LayoutDashboard size={18} className="mr-3" />ダッシュボード
           </div>
        </nav>
        <div className="p-4 mt-auto border-t border-gray-700">
          <button onClick={handleLogout} className="flex items-center text-gray-400 hover:text-white transition-colors w-full">
            <LogOut size={18} className="mr-3" />ログアウト
          </button>
        </div>
      </aside>
      <main className="flex-1 ml-64 p-8">
        <h1 className="text-2xl font-bold text-slate-800 mb-8">システム管理ダッシュボード</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <StatCard title="契約企業数" value={`${stats.totalCompanies}社`} icon={<Building2 size={24} />} color="bg-blue-500" />
            <StatCard title="課金対象ユーザー数" value={`${stats.totalUsers}名`} subValue={`（全登録：${stats.totalStaffCount}名）`} icon={<Users size={24} />} color="bg-indigo-500" />
            <StatCard title="予測月次売上（MRR）" value={`${stats.mrr.toLocaleString()}円`} subValue="↗ 前月比 +0%" icon={<CreditCard size={24} />} color="bg-teal-500" />
            <StatCard title="累計売上" value={`0円`} icon={<TrendingUp size={24} />} color="bg-orange-400" />
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4">
                <h2 className="text-lg font-bold text-slate-800">企業一覧・アカウント管理</h2>
                <div className="relative flex-1 md:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input type="text" placeholder="企業名・代表者名で検索..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" />
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 text-slate-500 font-bold text-xs">
                        <tr><th className="p-4">企業名 / 代表者名</th><th className="p-4">利用規模</th><th className="p-4">登録日 / プラン</th><th className="p-4">予測月次売上</th><th className="p-4">決済情報</th><th className="p-4 text-center">操作</th></tr>
                    </thead>
                    <tbody>
                        {isLoading ? ( <tr><td colSpan={6} className="p-8 text-center text-slate-400">読み込み中...</td></tr> ) : 
                         filteredCompanies.length === 0 ? ( <tr><td colSpan={6} className="p-8 text-center text-slate-400">データがありません</td></tr> ) : 
                        (filteredCompanies.map(company => {
                            const isTrial = getTrialStatus({ createdAt: company.joinedDate } as Store).isExpired === false && company.paymentStatus === 'incomplete';
                            const planText = company.plan === 'business' ? 'ビジネス' : 'スタンダード';
                            return (
                                <tr key={company.id} className="border-b border-gray-100 hover:bg-slate-50">
                                    <td className="p-4">
                                        <div className="font-bold text-slate-800">{company.name}</div>
                                        <div className="text-xs text-slate-500">{company.representativeName}</div>
                                    </td>
                                    <td className="p-4"><div className="font-bold text-slate-700">{company.storeCount}店舗</div><div className="text-xs text-slate-500">{company.userCount}名</div></td>
                                    <td className="p-4"><div>{formatDate(company.joinedDate)}</div><div>{isTrial ? <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded font-bold">無料期間中</span> : <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-bold">{planText}</span>}</div></td>
                                    <td className="p-4"><div className="font-bold">{isTrial ? 0 : company.monthlyRevenue.toLocaleString()}円</div><div className="text-xs text-slate-400">@{company.userCount}名</div></td>
                                    <td className="p-4">{company.paymentStatus === 'incomplete' ? <span className="text-[10px] font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded">未設定</span> : <span className="text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded">有効</span>}</td>
                                    <td className="p-4 text-center"><button className="p-2 hover:bg-white rounded-full text-gray-400"><MoreVertical size={18} /></button></td>
                                </tr>
                            );
                        }))}
                    </tbody>
                </table>
            </div>
        </div>
      </main>
    </div>
  );
};

export default SystemAdminPage;
